import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, SwitchCamera, Camera as CameraIcon } from "lucide-react";
import { useI18n } from "../context/I18nContext";

const MIN_DELAY = 1;
const MAX_DELAY = 10;
const BUFFER_PADDING_MS = 1200; // marge gardée en plus du délai max, pour rester fluide au changement de curseur

export default function DelayCamModal({ onClose }) {
  const { t } = useI18n();

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const streamRef = useRef(null);
  const bufferRef = useRef([]); // [{ t, bmp }]
  const delayRef = useRef(3);
  const captureRafRef = useRef(null);
  const renderRafRef = useRef(null);
  const runningRef = useRef(false);

  const [delay, setDelay] = useState(3);
  const [devices, setDevices] = useState([]);
  const [deviceIndex, setDeviceIndex] = useState(0);
  const [status, setStatus] = useState("starting"); // starting | live | denied | error | no_camera

  useEffect(() => { delayRef.current = delay; }, [delay]);

  const clearBuffer = () => {
    bufferRef.current.forEach((f) => { try { f.bmp.close(); } catch { /* */ } });
    bufferRef.current = [];
  };

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    }
  }, []);

  const stopLoops = useCallback(() => {
    runningRef.current = false;
    if (captureRafRef.current) cancelAnimationFrame(captureRafRef.current);
    if (renderRafRef.current) cancelAnimationFrame(renderRafRef.current);
    captureRafRef.current = null;
    renderRafRef.current = null;
  }, []);

  const drawCover = (ctx, bmp, cw, ch) => {
    const ir = bmp.width / bmp.height;
    const cr = cw / ch;
    let sw, sh, sx, sy;
    if (ir > cr) {
      sh = bmp.height;
      sw = sh * cr;
      sx = (bmp.width - sw) / 2;
      sy = 0;
    } else {
      sw = bmp.width;
      sh = sw / cr;
      sx = 0;
      sy = (bmp.height - sh) / 2;
    }
    ctx.drawImage(bmp, sx, sy, sw, sh, 0, 0, cw, ch);
  };

  const captureLoop = useCallback(() => {
    if (!runningRef.current || !videoRef.current) return;
    const video = videoRef.current;
    if (video.readyState >= 2) {
      createImageBitmap(video)
        .then((bmp) => {
          if (!runningRef.current) { bmp.close(); return; }
          const now = performance.now();
          bufferRef.current.push({ t: now, bmp });
          const maxAge = (MAX_DELAY * 1000) + BUFFER_PADDING_MS;
          while (bufferRef.current.length > 1 && now - bufferRef.current[0].t > maxAge) {
            const old = bufferRef.current.shift();
            try { old.bmp.close(); } catch { /* */ }
          }
        })
        .catch(() => { /* frame skip, pas grave */ });
    }
    captureRafRef.current = requestAnimationFrame(captureLoop);
  }, []);

  const renderLoop = useCallback(() => {
    if (!runningRef.current) return;
    const canvas = canvasRef.current;
    const buf = bufferRef.current;
    if (canvas && buf.length) {
      const ctx = canvas.getContext("2d");
      const now = performance.now();
      const targetT = now - delayRef.current * 1000;
      let frame = null;
      for (let i = buf.length - 1; i >= 0; i--) {
        if (buf[i].t <= targetT) { frame = buf[i]; break; }
      }
      if (!frame) frame = buf[0];
      drawCover(ctx, frame.bmp, canvas.width, canvas.height);
    }
    renderRafRef.current = requestAnimationFrame(renderLoop);
  }, []);

  const sizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = wrap.clientWidth * dpr;
    canvas.height = wrap.clientHeight * dpr;
  }, []);

  const startCamera = useCallback(async (deviceId) => {
    stopLoops();
    stopStream();
    clearBuffer();
    setStatus("starting");
    try {
      const constraints = {
        audio: false,
        video: deviceId
          ? { deviceId: { exact: deviceId } }
          : { facingMode: { ideal: "environment" } },
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }

      // Liste des caméras (les labels ne sont dispo qu'une fois la permission accordée)
      const all = await navigator.mediaDevices.enumerateDevices();
      const cams = all.filter((d) => d.kind === "videoinput");
      setDevices(cams);
      if (deviceId) {
        const idx = cams.findIndex((c) => c.deviceId === deviceId);
        if (idx >= 0) setDeviceIndex(idx);
      } else {
        const activeId = stream.getVideoTracks()[0]?.getSettings()?.deviceId;
        const idx = cams.findIndex((c) => c.deviceId === activeId);
        if (idx >= 0) setDeviceIndex(idx);
      }

      sizeCanvas();
      runningRef.current = true;
      captureRafRef.current = requestAnimationFrame(captureLoop);
      renderRafRef.current = requestAnimationFrame(renderLoop);
      setStatus("live");
    } catch (err) {
      if (err && (err.name === "NotAllowedError" || err.name === "SecurityError")) {
        setStatus("denied");
      } else if (err && err.name === "NotFoundError") {
        setStatus("no_camera");
      } else {
        setStatus("error");
      }
    }
  }, [captureLoop, renderLoop, sizeCanvas, stopLoops, stopStream]);

  useEffect(() => {
    startCamera();
    const onResize = () => sizeCanvas();
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      stopLoops();
      stopStream();
      clearBuffer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const switchCamera = () => {
    if (devices.length < 2) return;
    const next = (deviceIndex + 1) % devices.length;
    startCamera(devices[next].deviceId);
  };

  const close = () => {
    stopLoops();
    stopStream();
    clearBuffer();
    onClose();
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 60, background: "#000" }}>
      <div ref={wrapRef} style={{ position: "absolute", inset: 0 }}>
        <video ref={videoRef} muted playsInline style={{ display: "none" }} />
        <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block" }} />

        {status !== "live" && (
          <div className="absolute inset-0 flex items-center justify-center px-6">
            <div className="text-center max-w-xs">
              {status === "starting" && (
                <div className="text-zinc-400 text-sm uppercase tracking-[0.2em] font-bold">
                  {t("delaycam.starting")}
                </div>
              )}
              {status === "denied" && (
                <div className="text-red-400 text-sm font-bold">{t("delaycam.permission_denied")}</div>
              )}
              {status === "no_camera" && (
                <div className="text-zinc-400 text-sm font-bold">{t("delaycam.no_camera")}</div>
              )}
              {status === "error" && (
                <div className="text-red-400 text-sm font-bold">{t("delaycam.permission_denied")}</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Croix de fermeture — haut gauche, glass */}
      <button
        onClick={close}
        data-testid="delaycam-close"
        className="absolute top-4 left-4 z-10 flex items-center justify-center w-11 h-11 rounded-full bg-black/30 hover:bg-black/50 backdrop-blur-xl border border-white/10 text-white transition-colors"
      >
        <X size={20} />
      </button>

      {/* Bouton switch caméra — haut droite, glass */}
      {devices.length > 1 && (
        <button
          onClick={switchCamera}
          data-testid="delaycam-switch"
          title={t("delaycam.switch_camera")}
          className="absolute top-4 right-4 z-10 flex items-center justify-center w-11 h-11 rounded-full bg-black/30 hover:bg-black/50 backdrop-blur-xl border border-white/10 text-white transition-colors"
        >
          <SwitchCamera size={20} />
        </button>
      )}

      {/* Panneau délai — bas, glass */}
      <div className="absolute left-0 right-0 bottom-0 z-10 px-4 pb-6 pt-10 bg-gradient-to-t from-black/70 to-transparent">
        <div className="max-w-sm mx-auto bg-zinc-900/60 backdrop-blur-xl border border-white/10 rounded-2xl px-5 py-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-white/80 text-[11px] uppercase tracking-[0.2em] font-bold">
              <CameraIcon size={14} />
              {t("delaycam.delay_label")}
            </div>
            <div className="font-heading font-black text-cyan-400 text-xl tabular-nums">{delay}s</div>
          </div>
          <input
            type="range"
            min={MIN_DELAY}
            max={MAX_DELAY}
            step={1}
            value={delay}
            onChange={(e) => setDelay(parseInt(e.target.value, 10))}
            className="w-full accent-cyan-400"
            data-testid="delaycam-delay-slider"
          />
        </div>
      </div>
    </div>
  );
}
